/**
 * view-as.context.tsx — role view-as overlay for admin users (TOG-01).
 *
 * Provides a downgrade-only UI overlay that lets an admin user see the
 * application as a technician would. The overlay:
 *  - Persists across in-app navigation via localStorage (`marco.viewAs`).
 *  - Is cleared automatically on logout.
 *  - Does NOT modify the JWT `rol` claim. Server-side guards always read
 *    the real claim. This context governs rendered views and client routing only.
 *
 * Security boundary: only admin users can activate the overlay, and the overlay
 * can only downgrade (admin → technician). A technician cannot escalate via this
 * mechanism (enforced by resolveViewRole in auth/view-role.ts).
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "./hooks/useAuth.js";
import type { AppRole } from "./view-role.js";

const STORAGE_KEY = "marco.viewAs";

interface ViewAsContextValue {
  /** The current role override, or null when no override is active. */
  viewAs: AppRole | null;
  /** Activate the view-as overlay (admin only; noop for technicians). */
  setViewAs: (role: AppRole | null) => void;
}

const ViewAsContext = createContext<ViewAsContextValue | null>(null);

function loadViewAs(): AppRole | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "admin" || raw === "technician") return raw;
    return null;
  } catch {
    return null;
  }
}

export function ViewAsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const [viewAs, setViewAsState] = useState<AppRole | null>(() => loadViewAs());

  // Clear the overlay on logout (isAuthenticated goes false).
  useEffect(() => {
    if (!isAuthenticated) {
      localStorage.removeItem(STORAGE_KEY);
      setViewAsState(null);
    }
  }, [isAuthenticated]);

  const setViewAs = useCallback((role: AppRole | null) => {
    if (role === null) {
      // Clearing the override is always allowed.
      localStorage.removeItem(STORAGE_KEY);
      setViewAsState(null);
      return;
    }
    // Defense-in-depth: only admins may activate a non-null override.
    // resolveViewRole + AdminGuard already block escalation server-side and in
    // routing, but this closes the setter so non-admins can never write the key.
    if (user?.rol !== "admin") return;
    localStorage.setItem(STORAGE_KEY, role);
    setViewAsState(role);
  }, [user]);

  return (
    <ViewAsContext.Provider value={{ viewAs, setViewAs }}>
      {children}
    </ViewAsContext.Provider>
  );
}

export function useViewAs(): ViewAsContextValue {
  const ctx = useContext(ViewAsContext);
  if (!ctx) {
    throw new Error("useViewAs must be used within a ViewAsProvider");
  }
  return ctx;
}
